"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";
import { Plus, Loader2, Save, Upload } from "lucide-react";
import * as XLSX from "xlsx";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";

const USER_RESPONSES_FILE = "Execution_RFO_UserResponses.json";
const USER_RESPONSES_SECTION = "Execution_RFO_UserResponses";

/** Revenue from operations — FS note line id per framework (FinData_TrialBalance_Mapping) */
const NOTE_LINE_REVENUE_FS_AS = 30139;
const NOTE_LINE_REVENUE_FS_IND_AS = 30157;
const FRF_IND_AS = "Indian Accounting Standards (Ind AS)";
const FRF_AS = "Accounting Standards (AS)";

interface FsLedgerOption {
  name: string;
  amount: number;
}

interface CostPlusReconciliationPersisted {
  selectedFsLedgers: string[];
  user_rows: Array<{ reason: string; amount: number }>;
  frf?: string | null;
  activeNoteLineId?: number;
  lastUpdated?: string;
}

type TrialBalanceEntry = {
  ledger_id?: number;
  ledger_name?: string;
  note_line?: string;
  note_line_id?: number;
  fs_sub_line?: string;
  fs_sub_line_id?: number;
  amount?: Record<string, number | string>;
};

type TrialBalanceMappingData = Record<string, TrialBalanceEntry[]>;

export type CostPlusComputationRow = {
  id: string;
  bookNames: string[];
  fsSubLines: string[];
  noteLines: string[];
  ledgers: string[];
  markupPercent: string;
};

/** Parsed from Cost Plus Excel upload — populates Reconciliation without TB row inputs. */
export type CostPlusExcelDisaggregationLevel = {
  label: string;
  totalAmount: number;
  percentage: number;
  revenueToBeRecorded: number;
};

function cleanLedgerName(raw: unknown): string {
  let s = (raw ?? "").toString();
  s = s.replace(/_x000D/g, "").replace(/\n/g, "").replace(/_+$/, "").trim();
  return s;
}

function parseMappingDate(dateStr: string): Date {
  const [day, month, year] = dateStr.split("/");
  const y = year.length <= 2 ? 2000 + parseInt(year, 10) : parseInt(year, 10);
  return new Date(y, parseInt(month, 10) - 1, parseInt(day, 10));
}

function getLatestAmountDateKey(mappingData: TrialBalanceMappingData | null): string | null {
  if (!mappingData) return null;
  const keys = new Set<string>();
  for (const entries of Object.values(mappingData)) {
    if (!Array.isArray(entries)) continue;
    for (const e of entries) {
      if (e.amount && typeof e.amount === "object") {
        Object.keys(e.amount).forEach((k) => keys.add(k));
      }
    }
  }
  const arr = Array.from(keys);
  if (arr.length === 0) return null;
  return arr.sort((a, b) => parseMappingDate(b).getTime() - parseMappingDate(a).getTime())[0];
}

function newRowId(): string {
  return `cprow_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

async function loadTrialBalanceMapping(): Promise<TrialBalanceMappingData | null> {
  try {
    const api = (window as any).sharePointAPI;
    if (api?.downloadTrialBalanceMapping) {
      const r = await api.downloadTrialBalanceMapping({ includeAmounts: true });
      if (r?.success && r?.data && typeof r.data === "object") {
        return r.data as TrialBalanceMappingData;
      }
    }
    if (api?.downloadClientJson) {
      const r = await api.downloadClientJson("FinData_TrialBalance_Mapping.json");
      if (r?.success && r?.data && typeof r.data === "object") {
        return r.data as TrialBalanceMappingData;
      }
    }
  } catch (e) {
    console.warn("[RFO Cost Plus] Failed to load FinData_TrialBalance_Mapping", e);
  }
  return null;
}

function getBookNames(mappingData: TrialBalanceMappingData | null): string[] {
  if (!mappingData) return [];
  return Object.keys(mappingData)
    .filter((k) => Array.isArray(mappingData[k]) && mappingData[k].length > 0)
    .sort();
}

function getFsSubLinesForBooks(mappingData: TrialBalanceMappingData | null, books: string[]): string[] {
  if (!mappingData || books.length === 0) return [];
  const set = new Set<string>();
  for (const b of books) {
    const entries = mappingData[b];
    if (!Array.isArray(entries)) continue;
    for (const e of entries) {
      const fs = (e.fs_sub_line || (e as any).fsSubLine || "").toString().trim();
      if (fs) set.add(fs);
    }
  }
  return Array.from(set).sort();
}

function getNoteLinesForBooksAndFs(
  mappingData: TrialBalanceMappingData | null,
  books: string[],
  fsSubLines: string[],
): string[] {
  if (!mappingData || books.length === 0 || fsSubLines.length === 0) return [];
  const fsSet = new Set(fsSubLines);
  const set = new Set<string>();
  for (const b of books) {
    const entries = mappingData[b];
    if (!Array.isArray(entries)) continue;
    for (const e of entries) {
      const fs = (e.fs_sub_line || (e as any).fsSubLine || "").toString().trim();
      if (!fsSet.has(fs)) continue;
      const nl = (e.note_line || (e as any).noteLine || "").toString().trim();
      if (nl) set.add(nl);
    }
  }
  return Array.from(set).sort();
}

function getLedgersForFilters(
  mappingData: TrialBalanceMappingData | null,
  books: string[],
  fsSubLines: string[],
  noteLines: string[],
): string[] {
  if (!mappingData || books.length === 0 || fsSubLines.length === 0 || noteLines.length === 0) return [];
  const fsSet = new Set(fsSubLines);
  const nlSet = new Set(noteLines);
  const set = new Set<string>();
  for (const b of books) {
    const entries = mappingData[b];
    if (!Array.isArray(entries)) continue;
    for (const e of entries) {
      const fs = (e.fs_sub_line || (e as any).fsSubLine || "").toString().trim();
      if (!fsSet.has(fs)) continue;
      const nl = (e.note_line || (e as any).noteLine || "").toString().trim();
      if (!nlSet.has(nl)) continue;
      const name = cleanLedgerName(e.ledger_name || (e as any).ledgerName);
      if (name) set.add(name);
    }
  }
  return Array.from(set).sort();
}

function sumRowAmount(
  mappingData: TrialBalanceMappingData | null,
  latestDate: string | null,
  row: CostPlusComputationRow,
): number {
  if (!mappingData || !latestDate) return 0;
  const { bookNames, fsSubLines, noteLines, ledgers } = row;
  if (bookNames.length === 0 || fsSubLines.length === 0 || noteLines.length === 0 || ledgers.length === 0) {
    return 0;
  }
  const fsSet = new Set(fsSubLines);
  const nlSet = new Set(noteLines);
  const ledSet = new Set(ledgers);
  let sum = 0;
  for (const b of bookNames) {
    const entries = mappingData[b];
    if (!Array.isArray(entries)) continue;
    for (const e of entries) {
      const fs = (e.fs_sub_line || (e as any).fsSubLine || "").toString().trim();
      if (!fsSet.has(fs)) continue;
      const nl = (e.note_line || (e as any).noteLine || "").toString().trim();
      if (!nlSet.has(nl)) continue;
      const name = cleanLedgerName(e.ledger_name || (e as any).ledgerName);
      if (!ledSet.has(name)) continue;
      const raw = e.amount?.[latestDate];
      const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "0"));
      sum += Number.isFinite(n) ? n : 0;
    }
  }
  return sum;
}

function parseMarkupPercent(raw: string): number {
  const t = raw.trim().replace(/%/g, "");
  if (!t) return 0;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
}

function latestAmountForEntry(entry: TrialBalanceEntry): { date: string; amount: number } | null {
  const amountDict = entry.amount || {};
  const dates = Object.keys(amountDict);
  if (!dates.length) return null;
  const latestDate = dates.sort((a, b) => parseMappingDate(b).getTime() - parseMappingDate(a).getTime())[0];
  const raw = amountDict[latestDate];
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "0"));
  return { date: latestDate, amount: Number.isFinite(n) ? n : 0 };
}

function collectLedgersForNoteLineId(
  mappingData: TrialBalanceMappingData | null,
  noteLineId: number,
): FsLedgerOption[] {
  if (!mappingData) return [];
  const ledgerMap = new Map<string, number>();
  for (const ledgers of Object.values(mappingData)) {
    if (!Array.isArray(ledgers)) continue;
    for (const item of ledgers) {
      const nid = Number(item.note_line_id ?? (item as any).noteLineId);
      if (nid !== noteLineId) continue;
      const la = latestAmountForEntry(item);
      if (!la) continue;
      const name = cleanLedgerName(item.ledger_name || (item as any).ledgerName);
      if (!name) continue;
      ledgerMap.set(name, (ledgerMap.get(name) || 0) + la.amount);
    }
  }
  return Array.from(ledgerMap.entries())
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function resolveRevenueFsNoteLineId(frf: string | null | undefined): number {
  if (frf === FRF_IND_AS) return NOTE_LINE_REVENUE_FS_IND_AS;
  return NOTE_LINE_REVENUE_FS_AS;
}

function parseIndianNumberCell(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw ?? "")
    .replace(/,/g, "")
    .replace(/\s/g, "")
    .trim();
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function parsePercentageCell(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw ?? "")
    .replace(/%/g, "")
    .replace(/\s/g, "")
    .trim();
  if (!s) return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/** First sheet: column A = level label, B = Total Amount, C = Percentage, D = Revenue to be recorded. Skips a header row when detected. */
function parseCostPlusDisaggregationExcel(buffer: ArrayBuffer): CostPlusExcelDisaggregationLevel[] {
  const wb = XLSX.read(buffer, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const sheet = wb.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "" }) as unknown[][];
  if (!matrix.length) return [];

  const firstRow = matrix[0].map((c) => String(c).toLowerCase());
  const looksLikeHeader =
    firstRow.some((c) => c.includes("total") && c.includes("amount")) ||
    firstRow.some((c) => c.includes("revenue") && c.includes("record")) ||
    firstRow.some((c) => c.includes("percent")) ||
    firstRow.some((c) => c.includes("particular"));

  let start = 0;
  if (looksLikeHeader) start = 1;

  const out: CostPlusExcelDisaggregationLevel[] = [];
  for (let i = start; i < matrix.length; i++) {
    const row = matrix[i];
    if (!row || row.length < 2) continue;
    const label = String(row[0] ?? "").trim();
    if (!label) continue;
    const totalAmount = parseIndianNumberCell(row[1]);
    const percentage = row.length > 2 ? parsePercentageCell(row[2]) : 0;
    let revenueToBeRecorded =
      row.length > 3 ? parseIndianNumberCell(row[3]) : totalAmount * (100 + percentage) / 100;
    if (row.length <= 3 && totalAmount !== 0 && percentage !== 0) {
      revenueToBeRecorded = totalAmount * (100 + percentage) / 100;
    }
    out.push({ label, totalAmount, percentage, revenueToBeRecorded });
  }
  return out;
}

function pruneSelections(
  row: CostPlusComputationRow,
  mappingData: TrialBalanceMappingData | null,
): CostPlusComputationRow {
  const books = row.bookNames.filter((b) => getBookNames(mappingData).includes(b));
  const fsOpts = new Set(getFsSubLinesForBooks(mappingData, books));
  const fs = row.fsSubLines.filter((x) => fsOpts.has(x));
  const nlOpts = new Set(getNoteLinesForBooksAndFs(mappingData, books, fs));
  const nl = row.noteLines.filter((x) => nlOpts.has(x));
  const ledOpts = new Set(getLedgersForFilters(mappingData, books, fs, nl));
  const led = row.ledgers.filter((x) => ledOpts.has(x));
  return { ...row, bookNames: books, fsSubLines: fs, noteLines: nl, ledgers: led };
}

export default function RFOCostPlusComputation() {
  const { isArchivalMode } = useArchivalMode();
  const [mappingData, setMappingData] = useState<TrialBalanceMappingData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedResponsesLoaded, setSavedResponsesLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [frfLabel, setFrfLabel] = useState<string | null>(null);
  const [fsRevenueOptions, setFsRevenueOptions] = useState<FsLedgerOption[]>([]);
  const [fsRevenueLoading, setFsRevenueLoading] = useState(false);
  const [selectedFsRevenueLedgers, setSelectedFsRevenueLedgers] = useState<string[]>([]);
  const [isFsLedgerDropdownOpen, setIsFsLedgerDropdownOpen] = useState(false);
  const fsLedgerDropdownRef = useRef<HTMLDivElement>(null);
  const [userReconRows, setUserReconRows] = useState<Array<{ reason: string; amount: number }>>([]);
  const [rows, setRows] = useState<CostPlusComputationRow[]>(() => [
    {
      id: newRowId(),
      bookNames: [],
      fsSubLines: [],
      noteLines: [],
      ledgers: [],
      markupPercent: "",
    },
  ]);
  const [excelDisaggregationLevels, setExcelDisaggregationLevels] = useState<
    CostPlusExcelDisaggregationLevel[] | null
  >(null);
  const [excelUploadMessage, setExcelUploadMessage] = useState<string | null>(null);
  const [excelUploadError, setExcelUploadError] = useState<string | null>(null);
  const excelFileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const data = await loadTrialBalanceMapping();
    if (!data || Object.keys(data).length === 0) {
      setLoadError("Could not load FinData_TrialBalance_Mapping.json. Ensure it exists in the engagement juggernaut folder.");
      setMappingData(null);
    } else {
      setMappingData(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadSaved = async () => {
      try {
        const r = await (window as any).sharePointAPI?.downloadClientJson?.(USER_RESPONSES_FILE);
        if (r?.success && r?.data && typeof r.data === "object" && !Array.isArray(r.data)) {
          const cp = (r.data as any).cost_plus_comp;
          const rawRows = cp?.rows;
          if (Array.isArray(rawRows) && rawRows.length > 0) {
            const loaded: CostPlusComputationRow[] = rawRows.map((row: any) => ({
              id: typeof row.id === "string" && row.id ? row.id : newRowId(),
              bookNames: Array.isArray(row.bookNames) ? row.bookNames.map(String) : [],
              fsSubLines: Array.isArray(row.fsSubLines) ? row.fsSubLines.map(String) : [],
              noteLines: Array.isArray(row.noteLines) ? row.noteLines.map(String) : [],
              ledgers: Array.isArray(row.ledgers) ? row.ledgers.map(String) : [],
              markupPercent: row.markupPercent != null ? String(row.markupPercent) : "",
            }));
            setRows(loaded);
          }
          const rec = cp?.reconciliation;
          if (rec && typeof rec === "object") {
            if (Array.isArray((rec as CostPlusReconciliationPersisted).selectedFsLedgers)) {
              setSelectedFsRevenueLedgers(
                (rec as CostPlusReconciliationPersisted).selectedFsLedgers.map(String),
              );
            } else if (typeof (rec as any).fs_revenue_ledger === "string" && (rec as any).fs_revenue_ledger) {
              setSelectedFsRevenueLedgers([String((rec as any).fs_revenue_ledger)]);
            }
            const ur = (rec as any).user_rows;
            if (Array.isArray(ur)) {
              setUserReconRows(
                ur.map((r: any) => ({
                  reason: r.reason || r.particulars || "",
                  amount: Number(r.amount) || 0,
                })),
              );
            }
          }
          const excelLevels = (cp as any)?.excel_disaggregation_levels;
          if (Array.isArray(excelLevels) && excelLevels.length > 0) {
            setExcelDisaggregationLevels(
              excelLevels.map((row: any) => ({
                label: String(row.label ?? row.particulars ?? "").trim() || "Disaggregation",
                totalAmount: Number(row.totalAmount ?? row.total_amount) || 0,
                percentage: Number(row.percentage ?? row.percent) || 0,
                revenueToBeRecorded:
                  Number(row.revenueToBeRecorded ?? row.revenue_to_be_recorded ?? row.revenue) || 0,
              })),
            );
          } else {
            setExcelDisaggregationLevels(null);
          }
        }
      } catch (e) {
        console.warn("[RFO Cost Plus] No saved Execution_RFO_UserResponses.json yet or failed to load", e);
      } finally {
        setSavedResponsesLoaded(true);
      }
    };
    loadSaved();
  }, []);

  useEffect(() => {
    if (!mappingData) return;
    setRows((prev) => prev.map((r) => pruneSelections(r, mappingData)));
  }, [mappingData, savedResponsesLoaded]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!mappingData) {
        setFsRevenueOptions([]);
        setFrfLabel(null);
        return;
      }
      setFsRevenueLoading(true);
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? String(frfResult.frf || "").trim() : "";
        if (cancelled) return;
        setFrfLabel(frf || null);
        const noteLineId = resolveRevenueFsNoteLineId(frf || null);
        const opts = collectLedgersForNoteLineId(mappingData, noteLineId);
        if (!cancelled) setFsRevenueOptions(opts);
      } catch (e) {
        console.warn("[RFO Cost Plus] Failed to load FRF / FS revenue ledgers", e);
        if (!cancelled) {
          setFsRevenueOptions(collectLedgersForNoteLineId(mappingData, NOTE_LINE_REVENUE_FS_AS));
        }
      } finally {
        if (!cancelled) setFsRevenueLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [mappingData]);

  useEffect(() => {
    if (fsRevenueLoading || fsRevenueOptions.length === 0) return;
    setSelectedFsRevenueLedgers((prev) =>
      prev.filter((n) => fsRevenueOptions.some((o) => o.name === n)),
    );
  }, [fsRevenueOptions, fsRevenueLoading]);

  useEffect(() => {
    if (!isFsLedgerDropdownOpen) return;
    const onDown = (e: MouseEvent) => {
      const el = fsLedgerDropdownRef.current;
      if (el && !el.contains(e.target as Node)) {
        setIsFsLedgerDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isFsLedgerDropdownOpen]);

  const latestDate = useMemo(() => getLatestAmountDateKey(mappingData), [mappingData]);
  const bookOptions = useMemo(() => getBookNames(mappingData), [mappingData]);
  const activeFsNoteLineId = useMemo(() => resolveRevenueFsNoteLineId(frfLabel), [frfLabel]);

  const useExcelReconciliation =
    excelDisaggregationLevels != null && excelDisaggregationLevels.length > 0;

  const disaggregationMetrics = useMemo(() => {
    if (useExcelReconciliation && excelDisaggregationLevels) {
      return excelDisaggregationLevels.map((l) => ({
        amount: l.totalAmount,
        pct: l.percentage,
        revenue: l.revenueToBeRecorded,
      }));
    }
    if (!rows.length) {
      return [{ amount: 0, pct: 0, revenue: 0 }];
    }
    return rows.map((row) => {
      const amount = sumRowAmount(mappingData, latestDate, row);
      const pct = parseMarkupPercent(row.markupPercent);
      const revenue = amount * (100 + pct) / 100;
      return { amount, pct, revenue };
    });
  }, [useExcelReconciliation, excelDisaggregationLevels, rows, mappingData, latestDate]);

  const totalRevenueComputed = useMemo(
    () => disaggregationMetrics.reduce((s, m) => s + m.revenue, 0),
    [disaggregationMetrics],
  );

  const revenueAsPerFsTotal = useMemo(() => {
    if (!selectedFsRevenueLedgers.length || !fsRevenueOptions.length) return null;
    return fsRevenueOptions
      .filter((o) => selectedFsRevenueLedgers.includes(o.name))
      .reduce((s, o) => s + o.amount, 0);
  }, [fsRevenueOptions, selectedFsRevenueLedgers]);

  const numericDifference = useMemo(() => {
    if (revenueAsPerFsTotal === null) return null;
    return totalRevenueComputed - revenueAsPerFsTotal;
  }, [totalRevenueComputed, revenueAsPerFsTotal]);

  const userRowsSum = useMemo(
    () => userReconRows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [userReconRows],
  );

  const netDifference = useMemo(() => {
    if (numericDifference === null) return null;
    return numericDifference - userRowsSum;
  }, [numericDifference, userRowsSum]);

  const disaggregationColCount = Math.max(disaggregationMetrics.length, 1);

  const updateRow = (id: string, patch: Partial<CostPlusComputationRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        let next = { ...r, ...patch };
        if (patch.bookNames !== undefined) {
          next = {
            ...next,
            fsSubLines: [],
            noteLines: [],
            ledgers: [],
          };
        } else if (patch.fsSubLines !== undefined) {
          next = { ...next, noteLines: [], ledgers: [] };
        } else if (patch.noteLines !== undefined) {
          next = { ...next, ledgers: [] };
        }
        return pruneSelections(next, mappingData);
      }),
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: newRowId(),
        bookNames: [],
        fsSubLines: [],
        noteLines: [],
        ledgers: [],
        markupPercent: "",
      },
    ]);
  };

  const addUserReconRow = () => {
    setUserReconRows((prev) => [...prev, { reason: "", amount: 0 }]);
  };

  const updateUserReconRow = (index: number, field: "reason" | "amount", value: string) => {
    setUserReconRows((prev) => {
      const next = [...prev];
      if (field === "reason") {
        next[index] = { ...next[index], reason: value };
      } else {
        next[index] = { ...next[index], amount: Number(value) || 0 };
      }
      return next;
    });
  };

  const fmtMoney = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const saveCostPlus = async (excelLevelsOverride?: CostPlusExcelDisaggregationLevel[] | null) => {
    setSaveStatus("saving");
    setSaveError(null);
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }
      if (!(window as any).sharePointAPI?.uploadFile) {
        throw new Error("SharePoint upload is not available.");
      }

      const excelForPayload =
        excelLevelsOverride !== undefined ? excelLevelsOverride : excelDisaggregationLevels;

      let existing: Record<string, unknown> = {};
      try {
        const downloadResult = await (window as any).sharePointAPI.downloadClientJson(USER_RESPONSES_FILE);
        if (downloadResult?.success && downloadResult?.data && typeof downloadResult.data === "object" && !Array.isArray(downloadResult.data)) {
          existing = { ...downloadResult.data } as Record<string, unknown>;
        }
      } catch {
        // New file
      }

      const reconciliation: CostPlusReconciliationPersisted = {
        selectedFsLedgers: selectedFsRevenueLedgers,
        user_rows: userReconRows.map((r) => ({
          reason: r.reason,
          amount: Number(r.amount) || 0,
        })),
        frf: frfLabel,
        activeNoteLineId: activeFsNoteLineId,
        lastUpdated: new Date().toISOString(),
      };

      const cost_plus_comp = {
        rows: rows.map((r) => ({
          id: r.id,
          bookNames: r.bookNames,
          fsSubLines: r.fsSubLines,
          noteLines: r.noteLines,
          ledgers: r.ledgers,
          markupPercent: r.markupPercent,
          computedAmount: sumRowAmount(mappingData, latestDate, r),
        })),
        latestTbDate: latestDate,
        lastUpdated: new Date().toISOString(),
        reconciliation,
        excel_disaggregation_levels:
          excelForPayload && excelForPayload.length > 0 ? excelForPayload : null,
      };

      const merged = {
        ...existing,
        cost_plus_comp,
      };

      const jsonString = JSON.stringify(merged, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: USER_RESPONSES_FILE,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: [USER_RESPONSES_SECTION],
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Save failed.");
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (e) {
      console.error("[RFO Cost Plus] Save failed", e);
      setSaveError(e instanceof Error ? e.message : "Save failed.");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 5000);
    }
  };

  const handleSave = async () => {
    await saveCostPlus();
  };

  const handleExcelDisaggregationUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExcelUploadError(null);
    setExcelUploadMessage(null);
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setExcelUploadError("Please upload an Excel file (.xlsx or .xls).");
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const levels = parseCostPlusDisaggregationExcel(buf);
      if (!levels.length) {
        setExcelUploadError("No data rows found. Use columns: label, Total Amount, Percentage, Revenue to be recorded.");
        return;
      }
      setExcelDisaggregationLevels(levels);
      setExcelUploadMessage(`Loaded ${levels.length} level(s) from "${file.name}". Saving…`);
      await saveCostPlus(levels);
      setExcelUploadMessage(`Loaded ${levels.length} level(s) from "${file.name}" and saved.`);
    } catch (err) {
      console.error("[RFO Cost Plus] Excel upload failed", err);
      setExcelUploadError(err instanceof Error ? err.message : "Failed to read Excel file.");
    }
  };

  const disabled = isArchivalMode || loading;
  const reconDisabled = disabled || fsRevenueLoading;
  const reconFiguresReady = Boolean(latestDate) || useExcelReconciliation;

  return (
    <>
    <Card className="border-white/10 bg-white/5 text-white">
      <CardHeader className="space-y-2">
        <CardTitle>Cost Plus - Computation</CardTitle>
        <div className="rounded-md border border-white/10 bg-black/20 p-4 text-sm text-white/85">
          <p className="leading-relaxed">
            Disaggregate the data based on trial balance. (Kindly attach a file if the disaggregation is to be done on
            any other basis.)
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              ref={excelFileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              onChange={handleExcelDisaggregationUpload}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              className="border-white/20 text-white"
              onClick={() => excelFileInputRef.current?.click()}
            >
              <Upload className="mr-1 h-4 w-4" />
              Upload
            </Button>
            <span className="text-xs text-white/55">
              Excel: column A = level name, B = Total Amount, C = Percentage, D = Revenue to be recorded. Populates
              Reconciliation and saves automatically.
            </span>
            {excelUploadMessage && <span className="text-xs text-emerald-400/90">{excelUploadMessage}</span>}
            {excelUploadError && <span className="text-xs text-red-300/90">{excelUploadError}</span>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading trial balance mapping…
          </div>
        )}
        {loadError && !loading && <div className="text-sm text-amber-200/90">{loadError}</div>}
        {latestDate && !loading && (
          <p className="text-xs text-white/50">
            Amount uses the latest period column in the mapping file (currently <span className="text-white/70">{latestDate}</span>
            ).
          </p>
        )}

        <div className="overflow-x-auto rounded-md border border-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="min-w-[140px] text-white/90">Book Name</TableHead>
                <TableHead className="min-w-[180px] text-white/90">FS Sub-Line</TableHead>
                <TableHead className="min-w-[180px] text-white/90">Note Line</TableHead>
                <TableHead className="min-w-[200px] text-white/90">Ledger</TableHead>
                <TableHead className="min-w-[120px] text-right text-white/90">Amount</TableHead>
                <TableHead className="min-w-[160px] text-white/90">Mark-up % (on cost)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const fsOpts = getFsSubLinesForBooks(mappingData, row.bookNames);
                const nlOpts = getNoteLinesForBooksAndFs(mappingData, row.bookNames, row.fsSubLines);
                const ledOpts = getLedgersForFilters(mappingData, row.bookNames, row.fsSubLines, row.noteLines);
                const amount = sumRowAmount(mappingData, latestDate, row);
                return (
                  <TableRow key={row.id} className="border-white/10">
                    <TableCell className="align-top">
                      <MultiSelectDropdown
                        options={bookOptions}
                        value={row.bookNames}
                        onChange={(v) => updateRow(row.id, { bookNames: v })}
                        placeholder="Select book(s)…"
                        disabled={disabled}
                        loading={loading}
                        showSelectAll
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <MultiSelectDropdown
                        options={fsOpts}
                        value={row.fsSubLines}
                        onChange={(v) => updateRow(row.id, { fsSubLines: v })}
                        placeholder={row.bookNames.length ? "Select FS sub-line(s)…" : "Select book name first"}
                        disabled={disabled || row.bookNames.length === 0}
                        loading={loading}
                        showSelectAll
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <MultiSelectDropdown
                        options={nlOpts}
                        value={row.noteLines}
                        onChange={(v) => updateRow(row.id, { noteLines: v })}
                        placeholder={row.fsSubLines.length ? "Select note line(s)…" : "Select FS sub-line first"}
                        disabled={disabled || row.fsSubLines.length === 0}
                        loading={loading}
                        showSelectAll
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <MultiSelectDropdown
                        options={ledOpts}
                        value={row.ledgers}
                        onChange={(v) => updateRow(row.id, { ledgers: v })}
                        placeholder={row.noteLines.length ? "Select ledger(s)…" : "Select note line first"}
                        disabled={disabled || row.noteLines.length === 0}
                        loading={loading}
                        showSelectAll
                      />
                    </TableCell>
                    <TableCell className="align-top text-right font-mono text-sm tabular-nums">
                      {latestDate ? (
                        amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      ) : (
                        <span className="text-white/45">—</span>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Label htmlFor={`markup-${row.id}`} className="sr-only">
                        Mark-up percentage
                      </Label>
                      <Input
                        id={`markup-${row.id}`}
                        type="text"
                        inputMode="decimal"
                        placeholder="%"
                        value={row.markupPercent}
                        onChange={(e) => updateRow(row.id, { markupPercent: e.target.value })}
                        disabled={disabled}
                        className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            disabled={disabled}
            className="border-white/20 text-white"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add row
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={disabled || !savedResponsesLoaded || saveStatus === "saving"}
            className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/90"
          >
            {saveStatus === "saving" ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Save
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-emerald-400/90">Saved.</span>}
          {saveStatus === "error" && saveError && <span className="text-sm text-red-300/90">{saveError}</span>}
        </div>
      </CardContent>
    </Card>

    <Card className="border-white/10 bg-white/5 text-white">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <CardTitle className="text-lg">Reconciliation</CardTitle>
          <p className="text-xs text-white/50">
            {useExcelReconciliation ? (
              <>
                Reconciliation columns are populated from your uploaded Excel file (independent of the trial balance
                table above).
              </>
            ) : (
              <>
                Disaggregation level count matches the number of rows in the trial balance table above.
              </>
            )}{" "}
            {frfLabel ? (
              <>
                Revenue as per FS lists ledgers under note line{" "}
                <span className="text-white/70">{activeFsNoteLineId}</span>
                {frfLabel === FRF_IND_AS ? " (Ind AS)" : frfLabel === FRF_AS ? " (AS)" : ""} per engagement FRF.
              </>
            ) : (
              <>Default note line {NOTE_LINE_REVENUE_FS_AS} (AS) until FRF is loaded.</>
            )}
          </p>
        </div>
        {useExcelReconciliation && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={reconDisabled || saveStatus === "saving"}
            className="shrink-0 border-white/20 text-white"
            onClick={async () => {
              setExcelDisaggregationLevels(null);
              setExcelUploadMessage(null);
              setExcelUploadError(null);
              await saveCostPlus(null);
            }}
          >
            Clear Excel disaggregation
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {fsRevenueLoading && (
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading FRF and FS ledger options…</span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-white/10">
                <th className="border border-white/10 px-4 py-2 text-left font-medium text-white">Particulars</th>
                {disaggregationMetrics.length === 0 ? (
                  <th className="border border-white/10 px-4 py-2 text-right font-medium text-white">
                    Disaggregation Level 1
                  </th>
                ) : (
                  disaggregationMetrics.map((_, i) => (
                    <th
                      key={`recon-col-${i}`}
                      className="border border-white/10 px-4 py-2 text-right font-medium text-white"
                    >
                      {useExcelReconciliation && excelDisaggregationLevels?.[i]?.label
                        ? excelDisaggregationLevels[i].label
                        : `Disaggregation Level ${i + 1}`}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-white/10 px-4 py-2">Total Amount</td>
                {disaggregationMetrics.map((m, i) => (
                  <td key={i} className="border border-white/10 px-4 py-2 text-right font-mono tabular-nums">
                    {reconFiguresReady ? fmtMoney(m.amount) : "—"}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-white/10 px-4 py-2">Percentage</td>
                {disaggregationMetrics.map((m, i) => (
                  <td key={i} className="border border-white/10 px-4 py-2 text-right">
                    {disaggregationMetrics.length === 0 ? "—" : `${m.pct}%`}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="border border-white/10 px-4 py-2">Revenue to be recorded</td>
                {disaggregationMetrics.map((m, i) => (
                  <td key={i} className="border border-white/10 px-4 py-2 text-right font-mono tabular-nums">
                    {reconFiguresReady ? fmtMoney(m.revenue) : "—"}
                  </td>
                ))}
              </tr>
              <tr className="bg-white/10 font-medium">
                <td className="border border-white/10 px-4 py-2">Total Revenue</td>
                <td
                  className="border border-white/10 px-4 py-2 text-right font-mono tabular-nums"
                  colSpan={disaggregationColCount}
                >
                  {reconFiguresReady ? fmtMoney(totalRevenueComputed) : "—"}
                </td>
              </tr>
              <tr>
                <td className="border border-white/10 px-4 py-2">Revenue as per FS</td>
                <td className="border border-white/10 px-4 py-2 align-top" colSpan={disaggregationColCount}>
                  <div className="relative inline-block w-full text-left" ref={fsLedgerDropdownRef}>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between bg-black/40 border-white/20 text-white hover:bg-white/10"
                      onClick={() => setIsFsLedgerDropdownOpen((o) => !o)}
                      disabled={reconDisabled}
                    >
                      <span className="truncate">
                        {selectedFsRevenueLedgers.length === 0
                          ? "Select ledger(s)"
                          : `${selectedFsRevenueLedgers.length} selected`}
                      </span>
                    </Button>
                    {isFsLedgerDropdownOpen && !reconDisabled && (
                      <div className="absolute right-0 z-20 mt-2 w-full min-w-[280px] max-w-[480px] rounded-md border border-white/20 bg-black/90 shadow-lg max-h-64 overflow-auto">
                        {fsRevenueOptions.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-white/60">
                            No ledgers found for this note line in FinData_TrialBalance_Mapping.
                          </div>
                        ) : (
                          fsRevenueOptions.map((opt) => {
                            const checked = selectedFsRevenueLedgers.includes(opt.name);
                            return (
                              <button
                                key={opt.name}
                                type="button"
                                onClick={() => {
                                  setSelectedFsRevenueLedgers((prev) =>
                                    checked ? prev.filter((n) => n !== opt.name) : [...prev, opt.name],
                                  );
                                }}
                                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/10"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <span
                                    className={`h-4 w-4 shrink-0 rounded border ${
                                      checked ? "bg-emerald-500 border-emerald-400" : "border-white/40"
                                    }`}
                                  />
                                  <span className="truncate">{opt.name}</span>
                                </div>
                                <span className="shrink-0 text-xs text-white/60">{fmtMoney(opt.amount)}</span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
              <tr>
                <td className="border border-white/10 px-4 py-2">Difference</td>
                <td
                  className="border border-white/10 px-4 py-2 text-right font-mono tabular-nums"
                  colSpan={disaggregationColCount}
                >
                  {numericDifference != null ? fmtMoney(numericDifference) : "—"}
                </td>
              </tr>
              <tr className="bg-white/5">
                <td className="border border-white/10 px-4 py-2 font-medium text-white">User to reconcile</td>
                <td className="border border-white/10 px-4 py-2 text-right text-white" colSpan={disaggregationColCount}>
                  —
                </td>
              </tr>
              {userReconRows.map((row, index) => (
                <tr key={index}>
                  <td className="border border-white/10 px-4 py-2">
                    <Input
                      value={row.reason}
                      onChange={(e) => updateUserReconRow(index, "reason", e.target.value)}
                      className="border-white/10 bg-black/40 text-white"
                      placeholder="Particulars"
                      disabled={reconDisabled}
                    />
                  </td>
                  <td className="border border-white/10 px-4 py-2" colSpan={disaggregationColCount}>
                    <Input
                      type="number"
                      value={row.amount === 0 ? "" : row.amount}
                      onChange={(e) => updateUserReconRow(index, "amount", e.target.value)}
                      className="border-white/10 bg-black/40 text-right text-white"
                      placeholder="Amount"
                      disabled={reconDisabled}
                    />
                  </td>
                </tr>
              ))}
              <tr className="bg-white/5 font-medium">
                <td className="border border-white/10 px-4 py-2 text-white">Net Difference</td>
                <td
                  className="border border-white/10 px-4 py-2 text-right text-white font-mono tabular-nums"
                  colSpan={disaggregationColCount}
                >
                  {netDifference != null ? fmtMoney(netDifference) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addUserReconRow}
            disabled={reconDisabled}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Row
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={reconDisabled || !savedResponsesLoaded || saveStatus === "saving"}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && (
            <span className="text-sm text-green-400">Saved to {USER_RESPONSES_FILE}</span>
          )}
          {saveStatus === "error" && saveError && (
            <span className="text-sm text-red-400">{saveError}</span>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
}
